import { useRef, useMemo } from 'react';
import * as THREE from 'three';

// Ultra-slim minimalistic profile specifications
const PROFILE_SPECS = {
  W20: { 
    frameWidth: 0.015,   // 15mm - ultra minimal
    frameDepth: 0.040,   // 40mm depth
  },
  W40: { 
    frameWidth: 0.018,   // 18mm - slightly thicker
    frameDepth: 0.045,   // 45mm depth
  },
  FPS50: { 
    frameWidth: 0.022,   // 22mm - still slim
    frameDepth: 0.050,   // 50mm depth
  }
};

const COLOR_MAP = {
  'Black': '#0A0A0A',
  'White': '#FAFAFA',
  'Bronze': '#8B6F47',
  'Desert Sand': '#A6937E',
  'Soft Pink': '#E8C4C4',
  'Flaxpod': '#363636',
  'Charcoal': '#575F66'
};

// Glass texture component - renders different glass types
const GlassPanel = ({ width, height, glassType, position = [0, 0, 0] }) => {
  // Glass material properties based on type - MORE PRONOUNCED for visibility
  const getGlassProps = () => {
    switch (glassType) {
      case 'Clear':
        // Clear glass - transparent with noticeable blue tint
        return {
          color: '#7EC8E3',
          opacity: 0.35,
          transmission: 0.9,
          roughness: 0.05,
          metalness: 0.2
        };
      case 'Frosted':
        // Frosted glass - completely opaque white
        return {
          color: '#FFFFFF',
          opacity: 1,
          transmission: 0,
          roughness: 1,
          metalness: 0
        };
      case 'Fluted':
        // Fluted glass - grayish base for vertical lines
        return {
          color: '#C8D8D8',
          opacity: 0.6,
          transmission: 0.4,
          roughness: 0.6,
          metalness: 0.1
        };
      case 'Aquatex':
        // Aquatex - bumpy texture, more opaque
        return {
          color: '#B8C8C8',
          opacity: 0.75,
          transmission: 0.2,
          roughness: 0.9,
          metalness: 0.05
        };
      case 'Rain':
        // Rain glass - grayish for vertical streaks
        return {
          color: '#D0E0E0',
          opacity: 0.55,
          transmission: 0.5,
          roughness: 0.5,
          metalness: 0.1
        };
      case 'Flemish':
        // Flemish - organic pattern, more opaque
        return {
          color: '#C0D0D0',
          opacity: 0.7,
          transmission: 0.25,
          roughness: 0.8,
          metalness: 0.05
        };
      default:
        return {
          color: '#7EC8E3',
          opacity: 0.35,
          transmission: 0.9,
          roughness: 0.05,
          metalness: 0.2
        };
    }
  };

  const glassProps = getGlassProps();

  // For patterned glass, we add visual texture overlays - MORE PRONOUNCED
  const renderPatternOverlay = () => {
    const patternDepth = 0.002;
    
    switch (glassType) {
      case 'Fluted':
        // Bold vertical lines for fluted glass
        const flutedLines = [];
        const lineSpacing = 0.018; // Wider spacing, fewer lines
        const numLines = Math.floor(width / lineSpacing);
        for (let i = 0; i < numLines; i++) {
          const xPos = -width/2 + lineSpacing/2 + i * lineSpacing;
          flutedLines.push(
            <mesh key={`flute-${i}`} position={[xPos, 0, patternDepth]}>
              <boxGeometry args={[0.004, height - 0.004, 0.004]} />
              <meshStandardMaterial 
                color="#F8FCFC" 
                transparent 
                opacity={0.7}
                metalness={0.4}
                roughness={0.2}
              />
            </mesh>
          );
        }
        return <group>{flutedLines}</group>;

      case 'Rain':
        // Bold vertical rain streaks
        const rainLines = [];
        const rainSpacing = 0.015; // Wider spacing
        const numRainLines = Math.floor(width / rainSpacing);
        for (let i = 0; i < numRainLines; i++) {
          const xPos = -width/2 + rainSpacing/2 + i * rainSpacing;
          const lineHeight = height * (0.5 + Math.random() * 0.4);
          const yOffset = (height - lineHeight) / 2 * (Math.random() - 0.5) * 0.8;
          rainLines.push(
            <mesh key={`rain-${i}`} position={[xPos, yOffset, patternDepth]}>
              <boxGeometry args={[0.003, lineHeight - 0.004, 0.003]} />
              <meshStandardMaterial 
                color="#E8F0F0" 
                transparent 
                opacity={0.65}
                metalness={0.3}
                roughness={0.3}
              />
            </mesh>
          );
        }
        return <group>{rainLines}</group>;

      case 'Aquatex':
        // Bumpy/wavy pattern with visible dots
        const aquaElements = [];
        const dotSpacing = 0.04; // Larger spacing = fewer elements
        const numDotsX = Math.floor(width / dotSpacing);
        const numDotsY = Math.floor(height / dotSpacing);
        for (let i = 0; i < numDotsX; i++) {
          for (let j = 0; j < numDotsY; j++) {
            const xPos = -width/2 + dotSpacing/2 + i * dotSpacing + (Math.random() - 0.5) * 0.015;
            const yPos = -height/2 + dotSpacing/2 + j * dotSpacing + (Math.random() - 0.5) * 0.015;
            const dotSize = 0.008 + Math.random() * 0.006;
            aquaElements.push(
              <mesh key={`aqua-${i}-${j}`} position={[xPos, yPos, patternDepth]}>
                <circleGeometry args={[dotSize, 8]} />
                <meshStandardMaterial 
                  color="#F0F8F8" 
                  transparent 
                  opacity={0.6}
                  metalness={0.15}
                  roughness={0.5}
                  side={2}
                />
              </mesh>
            );
          }
        }
        return <group>{aquaElements}</group>;

      case 'Flemish':
        // Organic wavy pattern with visible rings
        const flemishElements = [];
        const ringSpacing = 0.05; // Larger spacing = fewer elements
        const numRingsX = Math.floor(width / ringSpacing);
        const numRingsY = Math.floor(height / ringSpacing);
        for (let i = 0; i < numRingsX; i++) {
          for (let j = 0; j < numRingsY; j++) {
            // Offset every other row
            const rowOffset = (j % 2) * (ringSpacing * 0.5);
            const xPos = -width/2 + i * ringSpacing + rowOffset + (Math.random() - 0.5) * 0.02;
            const yPos = -height/2 + ringSpacing/2 + j * ringSpacing + (Math.random() - 0.5) * 0.02;
            const ringSize = 0.012 + Math.random() * 0.008;
            flemishElements.push(
              <mesh key={`flem-${i}-${j}`} position={[xPos, yPos, patternDepth]}>
                <ringGeometry args={[ringSize * 0.5, ringSize, 12]} />
                <meshStandardMaterial 
                  color="#E8F0F0" 
                  transparent 
                  opacity={0.55}
                  metalness={0.1}
                  roughness={0.6}
                  side={2}
                />
              </mesh>
            );
          }
        }
        return <group>{flemishElements}</group>;

      default:
        return null;
    }
  };

  // Frosted glass needs a different material approach - use meshStandardMaterial for opaque look
  const isFrosted = glassType === 'Frosted';

  return (
    <group position={position}>
      {/* Base glass pane - visible from both sides */}
      <mesh castShadow>
        <planeGeometry args={[width, height]} />
        {isFrosted ? (
          <meshStandardMaterial
            color="#FFFFFF"
            transparent={false}
            roughness={1}
            metalness={0}
            side={2}
          />
        ) : (
          <meshPhysicalMaterial
            color={glassProps.color}
            transparent={true}
            opacity={glassProps.opacity}
            transmission={glassProps.transmission}
            roughness={glassProps.roughness}
            metalness={glassProps.metalness}
            thickness={0.02}
            side={2}
          />
        )}
      </mesh>
      
      {/* Pattern overlay for textured glass */}
      {renderPatternOverlay()}
    </group>
  );
};

// Outer Jamb Frame (around door unit)
const OuterJamb = ({ width, height, frameWidth, frameDepth, steelColor, hasThreshold, hasTransom, transomHeight }) => {
  const jambWidth = frameWidth * 1.5;
  const jambDepth = frameDepth * 1.2;
  
  // Door jamb stays at door height - transom extends ABOVE only
  // Left and right jambs extend upward if transom is present
  const sideJambHeight = hasTransom ? height + transomHeight : height;
  // The side jambs need to be offset upward to account for transom extension
  const sideJambYOffset = hasTransom ? transomHeight / 2 : 0;
  
  return (
    <group>
      {/* Left jamb - extends up for transom */}
      <mesh position={[-width/2 - jambWidth/2, sideJambYOffset, -jambDepth/4]} receiveShadow castShadow>
        <boxGeometry args={[jambWidth, sideJambHeight, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
      
      {/* Right jamb - extends up for transom */}
      <mesh position={[width/2 + jambWidth/2, sideJambYOffset, -jambDepth/4]} receiveShadow castShadow>
        <boxGeometry args={[jambWidth, sideJambHeight, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
      
      {/* Top jamb (head) - positioned at very top (above transom if present) */}
      <mesh position={[0, height/2 + (hasTransom ? transomHeight : 0) + jambWidth/2, -jambDepth/4]} receiveShadow castShadow>
        <boxGeometry args={[width + jambWidth * 2, jambWidth, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
      
      {/* Bottom jamb (threshold) - always at bottom of door, not affected by transom */}
      {hasThreshold && (
        <mesh position={[0, -height/2 - jambWidth/2, -jambDepth/4]} receiveShadow castShadow>
          <boxGeometry args={[width + jambWidth * 2, jambWidth, jambDepth]} />
          <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
        </mesh>
      )}
    </group>
  );
};

// TRUE ARCH OUTER JAMB - Uses same arch geometry as "Arched in Square"
// The arch apex is at the top of the door, radius = half the inner width
// This replaces the rectangular top jamb with a curved one
const TrueArchOuterJamb = ({ width, height, frameWidth, frameDepth, steelColor, hasThreshold }) => {
  const jambWidth = frameWidth * 1.5;
  const jambDepth = frameDepth * 1.2;
  
  // Inner dimensions (inside the jamb)
  const innerWidth = width;
  const innerHeight = height;
  
  // Arch geometry - SAME as Arched in Square
  // Radius = half the inner width, so arch ends meet the vertical jambs
  const archRadius = innerWidth / 2;
  
  // Arch center is at the top minus the radius (so apex touches top)
  const archCenterY = innerHeight / 2 - archRadius;
  
  // Base Y position
  const baseY = -innerHeight / 2;
  
  // Vertical jambs go from base up to where they meet the arch curve
  // At x = ±innerWidth/2, the arch height is at archCenterY (the spring line)
  const verticalJambHeight = archCenterY - baseY;
  
  // Generate curved arch segments (semicircle from 0 to π)
  const archSegments = [];
  const segments = 32;
  
  for (let i = 0; i < segments; i++) {
    const angle1 = (i / segments) * Math.PI;
    const angle2 = ((i + 1) / segments) * Math.PI;
    const midAngle = (angle1 + angle2) / 2;
    
    // Position on the outer arc (offset by jambWidth/2 for the outer edge)
    const outerRadius = archRadius + jambWidth / 2;
    const segX = Math.cos(midAngle) * outerRadius;
    const segY = archCenterY + Math.sin(midAngle) * outerRadius;
    
    // Segment length
    const segLength = 2 * outerRadius * Math.sin(Math.abs(angle2 - angle1) / 2) * 1.15;
    
    // Rotation tangent to arc
    const segRotation = midAngle - Math.PI / 2;
    
    archSegments.push(
      <mesh
        key={`arch-jamb-${i}`}
        position={[segX, segY, -jambDepth / 4]}
        rotation={[0, 0, segRotation]}
        castShadow
        receiveShadow
      >
        <boxGeometry args={[segLength, jambWidth, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
    );
  }
  
  return (
    <group>
      {/* Left vertical jamb - from base up to where arch curve starts */}
      <mesh position={[-innerWidth/2 - jambWidth/2, baseY + verticalJambHeight/2, -jambDepth/4]} receiveShadow castShadow>
        <boxGeometry args={[jambWidth, verticalJambHeight, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
      
      {/* Right vertical jamb - from base up to where arch curve starts */}
      <mesh position={[innerWidth/2 + jambWidth/2, baseY + verticalJambHeight/2, -jambDepth/4]} receiveShadow castShadow>
        <boxGeometry args={[jambWidth, verticalJambHeight, jambDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
      </mesh>
      
      {/* Curved arch at top */}
      {archSegments}
      
      {/* Bottom threshold */}
      {hasThreshold && (
        <mesh position={[0, baseY - jambWidth/2, -jambDepth/4]} receiveShadow castShadow>
          <boxGeometry args={[innerWidth + jambWidth * 2, jambWidth, jambDepth]} />
          <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
        </mesh>
      )}
    </group>
  );
};

// Helper function to calculate arch height at a given X position
const getArchHeightAtX = (x, archRadius, springLineY) => {
  // For a semicircle centered at (0, springLineY) with given radius
  // y = springLineY + sqrt(R² - x²) for |x| <= R
  if (Math.abs(x) > archRadius) return springLineY;
  return springLineY + Math.sqrt(archRadius * archRadius - x * x);
};

// Glass panel with curved top edge that follows the arch
// Used for topmost glass panels in True Arch configurations
const CurvedTopGlassPanel = ({ 
  width, 
  baseY,  // Global Y coordinate for bottom of glass
  glassType, 
  position = [0, 0, 0],
  // Arch parameters - coordinates in global space
  panelCenterX,  // X position of this panel's center in arch coordinate system
  archRadius,
  archCenterY
}) => {
  // Glass material properties based on type
  const getGlassProps = () => {
    switch (glassType) {
      case 'Clear':
        return { color: '#7EC8E3', opacity: 0.35, transmission: 0.9, roughness: 0.05, metalness: 0.2 };
      case 'Frosted':
        return { color: '#FFFFFF', opacity: 1, transmission: 0, roughness: 1, metalness: 0 };
      case 'Fluted':
        return { color: '#C8D8D8', opacity: 0.6, transmission: 0.4, roughness: 0.6, metalness: 0.1 };
      case 'Aquatex':
        return { color: '#B8C8C8', opacity: 0.75, transmission: 0.2, roughness: 0.9, metalness: 0.05 };
      case 'Rain':
        return { color: '#D0E0E0', opacity: 0.55, transmission: 0.5, roughness: 0.5, metalness: 0.1 };
      case 'Flemish':
        return { color: '#C0D0D0', opacity: 0.7, transmission: 0.25, roughness: 0.8, metalness: 0.05 };
      default:
        return { color: '#7EC8E3', opacity: 0.35, transmission: 0.9, roughness: 0.05, metalness: 0.2 };
    }
  };

  const glassProps = getGlassProps();
  const isFrosted = glassType === 'Frosted';
  
  // Create geometry using global coordinates directly
  const geometry = useMemo(() => {
    const halfWidth = width / 2;
    const segments = 32;
    
    // Create a Shape using global coordinates
    const shape = new THREE.Shape();
    
    // Start at bottom-left corner (global Y = baseY)
    shape.moveTo(-halfWidth, baseY);
    
    // Line to bottom-right
    shape.lineTo(halfWidth, baseY);
    
    // Draw the curved top from right to left following the arch
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const localX = halfWidth - t * width; // right to left in local coords
      const globalX = panelCenterX + localX; // position in arch coordinate system
      const archY = getArchHeightAtX(globalX, archRadius, archCenterY);
      shape.lineTo(localX, archY);
    }
    
    // Close the shape
    shape.closePath();
    
    // Create ShapeGeometry
    const geo = new THREE.ShapeGeometry(shape);
    geo.computeVertexNormals();
    
    return geo;
  }, [width, baseY, panelCenterX, archRadius, archCenterY]);
  
  return (
    <group position={position}>
      <mesh geometry={geometry} castShadow>
        {isFrosted ? (
          <meshStandardMaterial
            color="#FFFFFF"
            transparent={false}
            roughness={1}
            metalness={0}
            side={THREE.DoubleSide}
          />
        ) : (
          <meshPhysicalMaterial
            color={glassProps.color}
            transparent={true}
            opacity={glassProps.opacity}
            transmission={glassProps.transmission}
            roughness={glassProps.roughness}
            metalness={glassProps.metalness}
            thickness={0.02}
            side={THREE.DoubleSide}
          />
        )}
      </mesh>
    </group>
  );
};

// TRUE ARCH SIDELITE PANEL - Sidelite with top following arch curve
// Same interior as SidelitePanel but with curved top
const TrueArchSidelitePanel = ({
  width,
  height,
  frameWidth,
  frameDepth,
  steelColor,
  glass_type,
  panel_style,
  position,
  // Arch parameters
  panelLeftX,
  panelRightX,
  archRadius,
  archCenterY,
  baseY
}) => {
  // Calculate arch heights at panel edges (inside frame)
  const leftArchHeight = getArchHeightAtX(panelLeftX + frameWidth, archRadius, archCenterY);
  const rightArchHeight = getArchHeightAtX(panelRightX - frameWidth, archRadius, archCenterY);
  const centerArchHeight = getArchHeightAtX((panelLeftX + panelRightX) / 2, archRadius, archCenterY);
  
  // Height of left and right vertical frames
  const leftFrameHeight = leftArchHeight - baseY;
  const rightFrameHeight = rightArchHeight - baseY;
  
  // Generate curved top frame segments
  const topFrameSegments = [];
  const segments = 12;
  for (let i = 0; i < segments; i++) {
    const segLeftX = panelLeftX + frameWidth + (i / segments) * (width - frameWidth * 2);
    const segRightX = panelLeftX + frameWidth + ((i + 1) / segments) * (width - frameWidth * 2);
    const segCenterX = (segLeftX + segRightX) / 2;
    
    const leftH = getArchHeightAtX(segLeftX, archRadius, archCenterY);
    const rightH = getArchHeightAtX(segRightX, archRadius, archCenterY);
    const centerH = getArchHeightAtX(segCenterX, archRadius, archCenterY);
    
    if (centerH > archCenterY) {
      const angle = Math.atan2(rightH - leftH, segRightX - segLeftX);
      const segLength = Math.sqrt(Math.pow(segRightX - segLeftX, 2) + Math.pow(rightH - leftH, 2)) * 1.15;
      
      topFrameSegments.push(
        <mesh
          key={`top-frame-${i}`}
          position={[segCenterX - position[0], centerH - frameWidth/2, 0]}
          rotation={[0, 0, angle]}
          castShadow
        >
          <boxGeometry args={[segLength, frameWidth, frameDepth]} />
          <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
        </mesh>
      );
    }
  }
  
  // Glass panels - top panels follow arch curve
  const renderGlass = () => {
    const glassWidth = width - frameWidth * 2;
    const rectGlassHeight = height - frameWidth * 2;
    // Sidelite panel center X in arch coordinate system
    const panelCenterXInArch = (panelLeftX + panelRightX) / 2;
    const archTop = centerArchHeight - frameWidth;
    
    // Helper to check if a panel touches the arch
    const touchesArch = (panelTopY) => panelTopY >= archTop - 0.01;
    
    // For horizontal lites patterns
    if (panel_style === 'Horizontal Lites (3 Equal)' || panel_style === 'Horizontal Lites (3 Divisions)' || panel_style === 'Horizontal Lites') {
      const liteHeight = rectGlassHeight / 3;
      return [0, 1, 2].map((i) => {
        const liteBaseY = baseY + frameWidth + i * liteHeight;
        const liteTopY = liteBaseY + liteHeight;
        const glassBaseY = liteBaseY + frameWidth / 2;
        
        if (liteBaseY >= archTop) return null;
        
        const isTopPanel = touchesArch(liteTopY) || (i === 2);
        
        if (isTopPanel && liteBaseY < archTop) {
          // Use curved glass for top panel
          return (
            <group key={`lite-${i}`}>
              <CurvedTopGlassPanel
                width={glassWidth}
                baseY={glassBaseY}
                glassType={glass_type}
                position={[0, 0, 0]}
                panelCenterX={panelCenterXInArch}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
              {i < 2 && liteTopY < archTop && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        } else {
          // Regular rectangular glass for lower panels
          const glassHeight = liteHeight - frameWidth;
          return (
            <group key={`lite-${i}`}>
              <GlassPanel
                width={glassWidth}
                height={glassHeight}
                glassType={glass_type}
                position={[0, liteBaseY + liteHeight/2, 0]}
              />
              {i < 2 && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        }
      });
    }
    
    // For 2x2 grid
    if (panel_style === 'Grid Pattern (2x2)') {
      const panelHeight = rectGlassHeight / 2;
      const midY = baseY + frameWidth + panelHeight;
      const topRowBaseY = midY + frameWidth / 2;
      
      return (
        <>
          {/* Bottom row glass - always rectangular */}
          <GlassPanel
            width={glassWidth}
            height={panelHeight - frameWidth}
            glassType={glass_type}
            position={[0, baseY + frameWidth + (panelHeight - frameWidth)/2, 0]}
          />
          {/* Horizontal divider */}
          {midY < archTop && (
            <mesh position={[0, midY, 0]} castShadow>
              <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
              <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
            </mesh>
          )}
          {/* Top row glass - curved */}
          {midY + frameWidth < archTop && (
            <CurvedTopGlassPanel
              width={glassWidth}
              baseY={topRowBaseY}
              glassType={glass_type}
              position={[0, 0, 0]}
              panelCenterX={panelCenterXInArch}
              archRadius={archRadius}
              archCenterY={archCenterY - frameWidth}
            />
          )}
          {/* Vertical divider - extends to arch */}
          <mesh position={[0, baseY + frameWidth + Math.min(rectGlassHeight, archTop - baseY - frameWidth)/2, 0]} castShadow>
            <boxGeometry args={[frameWidth, Math.min(rectGlassHeight, archTop - baseY - frameWidth * 2), frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
        </>
      );
    }
    
    // Default: single glass pane with curved top
    const glassBaseY = baseY + frameWidth + frameWidth / 2;
    return (
      <CurvedTopGlassPanel
        width={glassWidth}
        baseY={glassBaseY}
        glassType={glass_type}
        position={[0, 0, 0]}
        panelCenterX={panelCenterXInArch}
        archRadius={archRadius}
        archCenterY={archCenterY - frameWidth}
      />
    );
  };
  
  return (
    <group position={position}>
      {/* Left Vertical Frame - extends to arch */}
      <mesh position={[-width/2 + frameWidth/2, baseY + leftFrameHeight/2, 0]} castShadow>
        <boxGeometry args={[frameWidth, leftFrameHeight, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>
      
      {/* Right Vertical Frame - extends to arch */}
      <mesh position={[width/2 - frameWidth/2, baseY + rightFrameHeight/2, 0]} castShadow>
        <boxGeometry args={[frameWidth, rightFrameHeight, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>
      
      {/* Bottom Frame */}
      <mesh position={[0, baseY + frameWidth/2, 0]} castShadow>
        <boxGeometry args={[width, frameWidth, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>
      
      {/* Curved top frame */}
      {topFrameSegments}
      
      {/* Glass panels */}
      {renderGlass()}
    </group>
  );
};

// TRUE ARCH DOOR PANEL - Door leaf with top following arch curve
// Includes full DoorPanel features: glass patterns, dividers, lockbox, hardware
const TrueArchDoorPanel = ({
  width,
  height,
  frameWidth,
  frameDepth,
  steelColor,
  glass_type,
  panel_style,
  isOpen,
  position,
  hingeOnLeft = true,
  hasLeverHandle = false,
  isDoubleDoor = false,
  // Arch parameters
  panelLeftX,
  panelRightX,
  archRadius,
  archCenterY,
  baseY
}) => {
  const openAngle = isOpen ? Math.PI * 0.5 : 0;
  const pivotX = hingeOnLeft ? -width/2 : width/2;
  const rotationDirection = hingeOnLeft ? 1 : -1;
  
  // Calculate arch heights at panel edges (inside frame)
  const leftArchHeight = getArchHeightAtX(panelLeftX + frameWidth, archRadius, archCenterY);
  const rightArchHeight = getArchHeightAtX(panelRightX - frameWidth, archRadius, archCenterY);
  const centerArchHeight = getArchHeightAtX((panelLeftX + panelRightX) / 2, archRadius, archCenterY);
  
  // Heights for vertical frames
  const leftFrameHeight = leftArchHeight - baseY;
  const rightFrameHeight = rightArchHeight - baseY;
  
  // Total glass area in rectangular portion
  const totalGlassHeight = height - frameWidth * 2;
  
  // Lockbox calculations (same as DoorPanel)
  let lockboxHeight, lockboxYPos, leverYOffset = 0;
  
  if (panel_style === 'Horizontal Lites (3 - Small Middle)') {
    const topHeight = totalGlassHeight * 0.4;
    const middleHeight = totalGlassHeight * 0.2;
    lockboxHeight = middleHeight - frameWidth * 0.5;
    lockboxYPos = baseY + frameWidth + topHeight + middleHeight/2;
  } else if (panel_style === 'Horizontal Lites (4 Equal)') {
    const liteHeight = totalGlassHeight / 4;
    lockboxHeight = liteHeight - frameWidth;
    lockboxYPos = baseY + frameWidth + liteHeight + liteHeight/2;
  } else if (panel_style === 'Grid Pattern (2x2)') {
    const panelHeight = totalGlassHeight / 2;
    lockboxHeight = panelHeight - frameWidth;
    lockboxYPos = baseY + frameWidth + panelHeight/2;
    leverYOffset = lockboxHeight * 0.35;
  } else if (panel_style === 'Grid Pattern (3x2)') {
    const panelHeight = totalGlassHeight / 3;
    lockboxHeight = panelHeight - frameWidth;
    lockboxYPos = baseY + frameWidth + panelHeight + panelHeight/2;
  } else {
    const liteHeight = totalGlassHeight / 3;
    lockboxHeight = liteHeight - frameWidth;
    lockboxYPos = baseY + frameWidth + liteHeight + liteHeight/2;
  }
  
  const lockboxWidth = frameWidth * 4.5;
  const lockboxDepth = frameDepth * 1.4;
  const leverLength = 0.10;
  const leverDiameter = 0.012;
  const lockboxXPos = hingeOnLeft ? width/2 - lockboxWidth/2 - 0.001 : -width/2 + lockboxWidth/2 + 0.001;
  
  // Generate curved top frame segments
  const topFrameSegments = [];
  const segments = 12;
  for (let i = 0; i < segments; i++) {
    const segLeftX = panelLeftX + frameWidth + (i / segments) * (width - frameWidth * 2);
    const segRightX = panelLeftX + frameWidth + ((i + 1) / segments) * (width - frameWidth * 2);
    const segCenterX = (segLeftX + segRightX) / 2;
    
    const leftH = getArchHeightAtX(segLeftX, archRadius, archCenterY);
    const rightH = getArchHeightAtX(segRightX, archRadius, archCenterY);
    const centerH = getArchHeightAtX(segCenterX, archRadius, archCenterY);
    
    if (centerH > archCenterY) {
      const angle = Math.atan2(rightH - leftH, segRightX - segLeftX);
      const segLength = Math.sqrt(Math.pow(segRightX - segLeftX, 2) + Math.pow(rightH - leftH, 2)) * 1.15;
      
      topFrameSegments.push(
        <mesh
          key={`top-frame-${i}`}
          position={[segCenterX - (panelLeftX + width/2), centerH - frameWidth/2, 0]}
          rotation={[0, 0, angle]}
          castShadow
        >
          <boxGeometry args={[segLength, frameWidth, frameDepth]} />
          <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
        </mesh>
      );
    }
  }
  
  // Render glass based on panel style - top panels follow arch curve
  const renderGlass = () => {
    const glassWidth = width - frameWidth * 2;
    const archTop = centerArchHeight - frameWidth;
    // Panel center X in arch coordinate system (for CurvedTopGlassPanel)
    const panelCenterXInArch = (panelLeftX + panelRightX) / 2;
    
    // Helper to check if a panel touches the arch
    const touchesArch = (panelTopY) => panelTopY >= archTop - 0.01;
    
    if (panel_style === 'Horizontal Lites (3 Equal)' || panel_style === 'Horizontal Lites (3 Divisions)' || panel_style === 'Horizontal Lites') {
      const liteHeight = totalGlassHeight / 3;
      return [0, 1, 2].map((i) => {
        const liteBaseY = baseY + frameWidth + i * liteHeight;
        const liteTopY = liteBaseY + liteHeight;
        const glassBaseY = liteBaseY + frameWidth / 2;
        const isTopPanel = touchesArch(liteTopY) || (i === 2);
        
        if (liteBaseY >= archTop) return null;
        
        if (isTopPanel && liteBaseY < archTop) {
          // Use curved glass for top panel
          return (
            <group key={`lite-${i}`}>
              <CurvedTopGlassPanel
                width={glassWidth}
                baseY={glassBaseY}
                glassType={glass_type}
                position={[0, 0, 0]}
                panelCenterX={panelCenterXInArch}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
              {i < 2 && liteTopY < archTop && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        } else {
          // Regular rectangular glass for lower panels
          const glassHeight = liteHeight - frameWidth;
          return (
            <group key={`lite-${i}`}>
              <GlassPanel
                width={glassWidth}
                height={glassHeight}
                glassType={glass_type}
                position={[0, liteBaseY + liteHeight/2, 0]}
              />
              {i < 2 && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        }
      });
    }
    
    if (panel_style === 'Horizontal Lites (3 - Small Middle)') {
      const heights = [totalGlassHeight * 0.4, totalGlassHeight * 0.2, totalGlassHeight * 0.4];
      let cumY = baseY + frameWidth;
      return heights.map((h, i) => {
        const liteBaseY = cumY;
        cumY += h;
        const liteTopY = cumY;
        const glassBaseY = liteBaseY + frameWidth / 2;
        
        if (liteBaseY >= archTop) return null;
        
        const isTopPanel = touchesArch(liteTopY) || (i === 2);
        
        if (isTopPanel && liteBaseY < archTop) {
          return (
            <group key={`lite-${i}`}>
              <CurvedTopGlassPanel
                width={glassWidth}
                baseY={glassBaseY}
                glassType={glass_type}
                position={[0, 0, 0]}
                panelCenterX={panelCenterXInArch}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
              {i < 2 && liteTopY < archTop && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        } else {
          const glassHeight = h - frameWidth;
          return (
            <group key={`lite-${i}`}>
              <GlassPanel
                width={glassWidth}
                height={glassHeight}
                glassType={glass_type}
                position={[0, liteBaseY + h/2, 0]}
              />
              {i < 2 && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        }
      });
    }
    
    if (panel_style === 'Horizontal Lites (4 Equal)') {
      const liteHeight = totalGlassHeight / 4;
      return [0, 1, 2, 3].map((i) => {
        const liteBaseY = baseY + frameWidth + i * liteHeight;
        const liteTopY = liteBaseY + liteHeight;
        const glassBaseY = liteBaseY + frameWidth / 2;
        
        if (liteBaseY >= archTop) return null;
        
        const isTopPanel = touchesArch(liteTopY) || (i === 3);
        
        if (isTopPanel && liteBaseY < archTop) {
          return (
            <group key={`lite-${i}`}>
              <CurvedTopGlassPanel
                width={glassWidth}
                baseY={glassBaseY}
                glassType={glass_type}
                position={[0, 0, 0]}
                panelCenterX={panelCenterXInArch}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
              {i < 3 && liteTopY < archTop && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        } else {
          const glassHeight = liteHeight - frameWidth;
          return (
            <group key={`lite-${i}`}>
              <GlassPanel
                width={glassWidth}
                height={glassHeight}
                glassType={glass_type}
                position={[0, liteBaseY + liteHeight/2, 0]}
              />
              {i < 3 && (
                <mesh position={[0, liteTopY - frameWidth/2, 0]} castShadow>
                  <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
              )}
            </group>
          );
        }
      });
    }
    
    if (panel_style === 'Grid Pattern (2x2)') {
      const cellWidth = glassWidth / 2;
      const cellHeight = totalGlassHeight / 2;
      const midY = baseY + frameWidth + cellHeight;
      const topRowBaseY = midY + frameWidth / 2;
      
      // Calculate X positions for left and right cells in arch coordinate system
      const leftCellCenterX = panelCenterXInArch - cellWidth / 2;
      const rightCellCenterX = panelCenterXInArch + cellWidth / 2;
      
      return (
        <>
          {/* Bottom row - always rectangular */}
          {[-1, 1].map((col) => (
            <GlassPanel
              key={`bottom-${col}`}
              width={cellWidth - frameWidth}
              height={cellHeight - frameWidth}
              glassType={glass_type}
              position={[col * cellWidth/2, baseY + frameWidth + (cellHeight - frameWidth)/2 + frameWidth/2, 0]}
            />
          ))}
          {/* Horizontal divider */}
          {midY < archTop && (
            <mesh position={[0, midY, 0]} castShadow>
              <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
              <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
            </mesh>
          )}
          {/* Top row - curved glass panels */}
          {midY + frameWidth < archTop && (
            <>
              {/* Left top cell */}
              <CurvedTopGlassPanel
                width={cellWidth - frameWidth}
                baseY={topRowBaseY}
                glassType={glass_type}
                position={[-cellWidth/2, 0, 0]}
                panelCenterX={leftCellCenterX}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
              {/* Right top cell */}
              <CurvedTopGlassPanel
                width={cellWidth - frameWidth}
                baseY={topRowBaseY}
                glassType={glass_type}
                position={[cellWidth/2, 0, 0]}
                panelCenterX={rightCellCenterX}
                archRadius={archRadius}
                archCenterY={archCenterY - frameWidth}
              />
            </>
          )}
          {/* Vertical divider - extends to arch */}
          <mesh position={[0, baseY + frameWidth + Math.min(totalGlassHeight, archTop - baseY - frameWidth)/2, 0]} castShadow>
            <boxGeometry args={[frameWidth, Math.min(totalGlassHeight - frameWidth, archTop - baseY - frameWidth * 2), frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
        </>
      );
    }
    
    if (panel_style === 'Grid Pattern (3x2)') {
      const cellWidth = glassWidth / 2;
      const cellHeight = totalGlassHeight / 3;
      const leftCellCenterX = panelCenterXInArch - cellWidth / 2;
      const rightCellCenterX = panelCenterXInArch + cellWidth / 2;
      
      return (
        <>
          {[0, 1, 2].map((row) => {
            const rowBaseY = baseY + frameWidth + row * cellHeight;
            const rowTopY = rowBaseY + cellHeight;
            const glassBaseY = rowBaseY + frameWidth / 2;
            
            if (rowBaseY >= archTop) return null;
            
            const isTopRow = touchesArch(rowTopY) || (row === 2);
            
            if (isTopRow && rowBaseY < archTop) {
              // Use curved glass for top row
              return (
                <group key={`row-${row}`}>
                  <CurvedTopGlassPanel
                    width={cellWidth - frameWidth}
                    baseY={glassBaseY}
                    glassType={glass_type}
                    position={[-cellWidth/2, 0, 0]}
                    panelCenterX={leftCellCenterX}
                    archRadius={archRadius}
                    archCenterY={archCenterY - frameWidth}
                  />
                  <CurvedTopGlassPanel
                    width={cellWidth - frameWidth}
                    baseY={glassBaseY}
                    glassType={glass_type}
                    position={[cellWidth/2, 0, 0]}
                    panelCenterX={rightCellCenterX}
                    archRadius={archRadius}
                    archCenterY={archCenterY - frameWidth}
                  />
                  {row < 2 && rowTopY < archTop && (
                    <mesh position={[0, rowTopY - frameWidth/2, 0]} castShadow>
                      <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                      <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                    </mesh>
                  )}
                </group>
              );
            } else {
              // Regular rectangular glass for lower rows
              const glassHeight = cellHeight - frameWidth;
              return (
                <group key={`row-${row}`}>
                  {[-1, 1].map((col) => (
                    <GlassPanel
                      key={`cell-${row}-${col}`}
                      width={cellWidth - frameWidth}
                      height={glassHeight}
                      glassType={glass_type}
                      position={[col * cellWidth/2, rowBaseY + cellHeight/2, 0]}
                    />
                  ))}
                  {row < 2 && (
                    <mesh position={[0, rowTopY - frameWidth/2, 0]} castShadow>
                      <boxGeometry args={[glassWidth, frameWidth, frameDepth]} />
                      <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                    </mesh>
                  )}
                </group>
              );
            }
          })}
          {/* Vertical divider */}
          <mesh position={[0, baseY + frameWidth + Math.min(totalGlassHeight, archTop - baseY - frameWidth)/2, 0]} castShadow>
            <boxGeometry args={[frameWidth, Math.min(totalGlassHeight - frameWidth, archTop - baseY - frameWidth * 2), frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
        </>
      );
    }
    
    // Default: single glass panel with curved top
    const glassBaseY = baseY + frameWidth + frameWidth / 2;
    return (
      <CurvedTopGlassPanel
        width={glassWidth}
        baseY={glassBaseY}
        glassType={glass_type}
        position={[0, 0, 0]}
        panelCenterX={panelCenterXInArch}
        archRadius={archRadius}
        archCenterY={archCenterY - frameWidth}
      />
    );
  };
  
  // Render lockbox and hardware
  const renderLockbox = () => {
    if (!hasLeverHandle) return null;
    
    return (
      <group position={[lockboxXPos, lockboxYPos, 0]}>
        <mesh castShadow>
          <boxGeometry args={[lockboxWidth, lockboxHeight, lockboxDepth]} />
          <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
        </mesh>
        <mesh position={[0, 0, lockboxDepth/2 + 0.001]} castShadow>
          <boxGeometry args={[lockboxWidth - 0.004, lockboxHeight - 0.004, 0.003]} />
          <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Front lever */}
        <group position={[0, leverYOffset, lockboxDepth/2]}>
          <group position={[0, 0, leverDiameter/2 + 0.008]}>
            <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
              <cylinderGeometry args={[0.016, 0.016, 0.008, 24]} />
              <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
            </mesh>
            <mesh position={[hingeOnLeft ? -leverLength/2 - 0.008 : leverLength/2 + 0.008, 0, 0]} castShadow>
              <boxGeometry args={[leverLength, leverDiameter, leverDiameter]} />
              <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
            </mesh>
          </group>
          <mesh position={[0, -lockboxHeight * 0.25 - leverYOffset, 0.006]} rotation={[Math.PI/2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.008, 0.008, 0.005, 24]} />
            <meshStandardMaterial color={steelColor} metalness={0.9} roughness={0.1} />
          </mesh>
        </group>
        {/* Back lever */}
        <group position={[0, 0, -lockboxDepth/2]} rotation={[0, Math.PI, 0]}>
          <mesh position={[0, 0, 0.001]} castShadow>
            <boxGeometry args={[lockboxWidth - 0.004, lockboxHeight - 0.004, 0.003]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
          <group position={[0, leverYOffset, leverDiameter/2 + 0.008]}>
            <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
              <cylinderGeometry args={[0.016, 0.016, 0.008, 24]} />
              <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
            </mesh>
            <mesh position={[hingeOnLeft ? leverLength/2 + 0.008 : -leverLength/2 - 0.008, 0, 0]} castShadow>
              <boxGeometry args={[leverLength, leverDiameter, leverDiameter]} />
              <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
            </mesh>
          </group>
          <mesh position={[0, -lockboxHeight * 0.25, 0.006]} rotation={[Math.PI/2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.008, 0.008, 0.005, 24]} />
            <meshStandardMaterial color={steelColor} metalness={0.9} roughness={0.1} />
          </mesh>
        </group>
      </group>
    );
  };
  
  return (
    <group position={[position[0] + pivotX, position[1], position[2]]}>
      <group rotation={[0, openAngle * rotationDirection, 0]}>
        <group position={[-pivotX, 0, 0]}>
          {/* Left Vertical Frame - extends to arch */}
          <mesh position={[-width/2 + frameWidth/2, baseY + leftFrameHeight/2, 0]} castShadow>
            <boxGeometry args={[frameWidth, leftFrameHeight, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* Right Vertical Frame - extends to arch */}
          <mesh position={[width/2 - frameWidth/2, baseY + rightFrameHeight/2, 0]} castShadow>
            <boxGeometry args={[frameWidth, rightFrameHeight, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* Bottom Frame */}
          <mesh position={[0, baseY + frameWidth/2, 0]} castShadow>
            <boxGeometry args={[width, frameWidth, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* Curved top frame */}
          {topFrameSegments}
          
          {/* Glass panels */}
          {renderGlass()}
          
          {/* Lockbox and hardware */}
          {renderLockbox()}
        </group>
      </group>
    </group>
  );
};

// TRUE ARCH DOOR UNIT - Complete door unit with arched perimeter
// Uses TrueArchDoorPanel and TrueArchSidelitePanel so all elements follow the arch curve
const TrueArchDoorUnit = ({
  totalWidth,
  doorWidth,
  doorHeight,
  frameWidth,
  frameDepth,
  steelColor,
  glass_type,
  panel_style,
  isDouble,
  hasThreshold,
  hasSideliteLeft,
  hasSideliteRight,
  sideliteWidth,
  isOpen = false,
  hasLeverHandle = false
}) => {
  const baseY = -doorHeight / 2;
  const topY = doorHeight / 2;
  
  // Arch geometry - same as Arched in Square
  const archRadius = totalWidth / 2;
  const archCenterY = topY - archRadius;
  
  // Calculate panel X positions
  const leftSideliteLeftX = -totalWidth / 2;
  const leftSideliteRightX = hasSideliteLeft ? leftSideliteLeftX + sideliteWidth : leftSideliteLeftX;
  
  const rightSideliteRightX = totalWidth / 2;
  const rightSideliteLeftX = hasSideliteRight ? rightSideliteRightX - sideliteWidth : rightSideliteRightX;
  
  // Door boundaries
  const doorLeftX = hasSideliteLeft ? leftSideliteRightX : -totalWidth / 2;
  const doorRightX = hasSideliteRight ? rightSideliteLeftX : totalWidth / 2;
  const doorCenterX = (doorLeftX + doorRightX) / 2;
  const actualDoorWidth = doorRightX - doorLeftX;
  
  if (isDouble) {
    const singleWidth = actualDoorWidth / 2;
    const leftDoorLeftX = doorLeftX;
    const leftDoorRightX = doorCenterX;
    const rightDoorLeftX = doorCenterX;
    const rightDoorRightX = doorRightX;
    
    return (
      <group>
        {/* Outer arched jamb */}
        <TrueArchOuterJamb
          width={totalWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          hasThreshold={hasThreshold}
        />
        
        {/* Left Sidelite with arch-following top */}
        {hasSideliteLeft && (
          <TrueArchSidelitePanel
            width={sideliteWidth}
            height={doorHeight}
            frameWidth={frameWidth}
            frameDepth={frameDepth}
            steelColor={steelColor}
            glass_type={glass_type}
            panel_style={panel_style}
            position={[-totalWidth/2 + sideliteWidth/2, 0, 0]}
            panelLeftX={leftSideliteLeftX}
            panelRightX={leftSideliteRightX}
            archRadius={archRadius}
            archCenterY={archCenterY}
            baseY={baseY}
          />
        )}
        
        {/* Right Sidelite with arch-following top */}
        {hasSideliteRight && (
          <TrueArchSidelitePanel
            width={sideliteWidth}
            height={doorHeight}
            frameWidth={frameWidth}
            frameDepth={frameDepth}
            steelColor={steelColor}
            glass_type={glass_type}
            panel_style={panel_style}
            position={[totalWidth/2 - sideliteWidth/2, 0, 0]}
            panelLeftX={rightSideliteLeftX}
            panelRightX={rightSideliteRightX}
            archRadius={archRadius}
            archCenterY={archCenterY}
            baseY={baseY}
          />
        )}
        
        {/* Left door leaf with arch-following top */}
        <TrueArchDoorPanel
          width={singleWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          glass_type={glass_type}
          panel_style={panel_style}
          isOpen={isOpen}
          position={[doorLeftX + singleWidth/2, 0, 0]}
          hingeOnLeft={true}
          hasLeverHandle={hasLeverHandle}
          isDoubleDoor={true}
          panelLeftX={leftDoorLeftX}
          panelRightX={leftDoorRightX}
          archRadius={archRadius}
          archCenterY={archCenterY}
          baseY={baseY}
        />
        
        {/* Right door leaf with arch-following top */}
        <TrueArchDoorPanel
          width={singleWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          glass_type={glass_type}
          panel_style={panel_style}
          isOpen={isOpen}
          position={[doorCenterX + singleWidth/2, 0, 0]}
          hingeOnLeft={false}
          hasLeverHandle={hasLeverHandle}
          isDoubleDoor={true}
          panelLeftX={rightDoorLeftX}
          panelRightX={rightDoorRightX}
          archRadius={archRadius}
          archCenterY={archCenterY}
          baseY={baseY}
        />
      </group>
    );
  } else {
    // Single door
    return (
      <group>
        {/* Outer arched jamb */}
        <TrueArchOuterJamb
          width={totalWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          hasThreshold={hasThreshold}
        />
        
        {/* Left Sidelite with arch-following top */}
        {hasSideliteLeft && (
          <TrueArchSidelitePanel
            width={sideliteWidth}
            height={doorHeight}
            frameWidth={frameWidth}
            frameDepth={frameDepth}
            steelColor={steelColor}
            glass_type={glass_type}
            panel_style={panel_style}
            position={[-totalWidth/2 + sideliteWidth/2, 0, 0]}
            panelLeftX={leftSideliteLeftX}
            panelRightX={leftSideliteRightX}
            archRadius={archRadius}
            archCenterY={archCenterY}
            baseY={baseY}
          />
        )}
        
        {/* Right Sidelite with arch-following top */}
        {hasSideliteRight && (
          <TrueArchSidelitePanel
            width={sideliteWidth}
            height={doorHeight}
            frameWidth={frameWidth}
            frameDepth={frameDepth}
            steelColor={steelColor}
            glass_type={glass_type}
            panel_style={panel_style}
            position={[totalWidth/2 - sideliteWidth/2, 0, 0]}
            panelLeftX={rightSideliteLeftX}
            panelRightX={rightSideliteRightX}
            archRadius={archRadius}
            archCenterY={archCenterY}
            baseY={baseY}
          />
        )}
        
        {/* Single door leaf with arch-following top */}
        <TrueArchDoorPanel
          width={actualDoorWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          glass_type={glass_type}
          panel_style={panel_style}
          isOpen={isOpen}
          position={[doorLeftX + actualDoorWidth/2, 0, 0]}
          hingeOnLeft={true}
          hasLeverHandle={hasLeverHandle}
          isDoubleDoor={false}
          panelLeftX={doorLeftX}
          panelRightX={doorRightX}
          archRadius={archRadius}
          archCenterY={archCenterY}
          baseY={baseY}
        />
      </group>
    );
  }
};

// Lock Plate with Lever Handles - hardware mounted on door frame meeting stile
const LockPlateWithHandles = ({ 
  doorHeight, 
  frameWidth, 
  frameDepth, 
  steelColor, 
  isOpen,
  isDouble = true 
}) => {
  // The "lock plate" is the meeting stile of the door frame - already part of the door
  // We just add the hardware (lever handles) mounted on it
  
  // Lever handles - short stubby levers as shown in reference
  const handleLength = 0.055;  // ~55mm - short stubby lever
  const handleDiameter = 0.010; // 10mm diameter bar
  
  // Small backplate/rosette where handle mounts
  const rosetteDiameter = 0.032; // 32mm rosette
  const rosetteDepth = 0.006;
  
  // Position at typical handle height (slightly below center)
  const handleYPosition = -doorHeight * 0.025;
  
  if (!isDouble) {
    // Single door - handle on lock side
    const handleXPos = doorHeight * 0.18;
    
    if (isOpen) return null;
    
    return (
      <group position={[handleXPos, handleYPosition, frameDepth/2 + rosetteDepth/2 + 0.002]}>
        {/* Rosette/backplate */}
        <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
          <cylinderGeometry args={[rosetteDiameter/2, rosetteDiameter/2, rosetteDepth, 32]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
        
        {/* Lever handle - short stubby bar pointing right */}
        <mesh position={[handleLength/2 + rosetteDiameter/3, 0, rosetteDepth/2 + handleDiameter/2]} castShadow>
          <boxGeometry args={[handleLength, handleDiameter, handleDiameter]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
        
        {/* Keyhole above - small cylinder */}
        <mesh position={[0, rosetteDiameter * 1.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
          <cylinderGeometry args={[0.008, 0.008, rosetteDepth, 32]} />
          <meshStandardMaterial color="#0A0A0A" metalness={0.95} roughness={0.05} />
        </mesh>
      </group>
    );
  }
  
  // Double door - handles on both leaves at meeting stile
  if (isOpen) return null;
  
  return (
    <group position={[0, handleYPosition, frameDepth/2 + rosetteDepth/2 + 0.002]}>
      {/* Left door handle - rosette and lever pointing left */}
      <group position={[-frameWidth/2, 0, 0]}>
        {/* Rosette */}
        <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
          <cylinderGeometry args={[rosetteDiameter/2, rosetteDiameter/2, rosetteDepth, 32]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
        {/* Lever - points left */}
        <mesh position={[-handleLength/2 - rosetteDiameter/3, 0, rosetteDepth/2 + handleDiameter/2]} castShadow>
          <boxGeometry args={[handleLength, handleDiameter, handleDiameter]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
        {/* Keyhole */}
        <mesh position={[0, rosetteDiameter * 1.2, 0]} rotation={[Math.PI/2, 0, 0]} castShadow>
          <cylinderGeometry args={[0.008, 0.008, rosetteDepth, 32]} />
          <meshStandardMaterial color="#0A0A0A" metalness={0.95} roughness={0.05} />
        </mesh>
      </group>
      
      {/* Right door handle - rosette and lever pointing right */}
      <group position={[frameWidth/2 + 0.002, 0, 0]}>
        {/* Rosette */}
        <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
          <cylinderGeometry args={[rosetteDiameter/2, rosetteDiameter/2, rosetteDepth, 32]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
        {/* Lever - points right */}
        <mesh position={[handleLength/2 + rosetteDiameter/3, 0, rosetteDepth/2 + handleDiameter/2]} castShadow>
          <boxGeometry args={[handleLength, handleDiameter, handleDiameter]} />
          <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
        </mesh>
      </group>
    </group>
  );
};

// Complete Door Panel - swings as one unit with integrated hardware
const DoorPanel = ({ 
  width, 
  height, 
  frameWidth, 
  frameDepth, 
  steelColor, 
  glass_type, 
  panel_style, 
  isOpen,
  position,
  hingeOnLeft = true,
  hasLeverHandle = false,
  isDoubleDoor = false,
  hasArchedInSquare = false
}) => {
  const openAngle = isOpen ? Math.PI * 0.5 : 0;
  const pivotX = hingeOnLeft ? -width/2 : width/2;
  const rotationDirection = hingeOnLeft ? 1 : -1;
  
  // Calculate total glass area height
  const totalGlassHeight = height - frameWidth * 2;
  
  // Calculate lockbox position and height based on panel style
  // Lockbox always sits within the horizontal grids, adapting to each style
  let lockboxHeight, lockboxYPos, leverYOffset = 0;
  
  if (panel_style === 'Horizontal Lites (3 - Small Middle)') {
    // 3 panels: 40% top, 20% middle, 40% bottom
    const topHeight = totalGlassHeight * 0.4;
    const middleHeight = totalGlassHeight * 0.2;
    lockboxHeight = middleHeight - frameWidth * 0.5;
    lockboxYPos = -height/2 + frameWidth + topHeight + middleHeight/2;
  } else if (panel_style === 'Horizontal Lites (4 Equal)') {
    // 4 equal panels - lockbox in second panel from bottom (panel index 1)
    const liteHeight = totalGlassHeight / 4;
    lockboxHeight = liteHeight - frameWidth;
    lockboxYPos = -height/2 + frameWidth + liteHeight + liteHeight/2;
  } else if (panel_style === 'Grid Pattern (2x2)') {
    // 2x2 grid - lockbox in lower half, but lever handles moved up toward center
    const panelHeight = totalGlassHeight / 2;
    lockboxHeight = panelHeight - frameWidth;
    lockboxYPos = -height/2 + frameWidth + panelHeight/2;
    // Move lever handles up closer to center of door (offset from lockbox center)
    leverYOffset = lockboxHeight * 0.35;
  } else if (panel_style === 'Grid Pattern (3x2)') {
    // 3x2 grid - lockbox in middle row
    const panelHeight = totalGlassHeight / 3;
    lockboxHeight = panelHeight - frameWidth;
    lockboxYPos = -height/2 + frameWidth + panelHeight + panelHeight/2;
  } else {
    // Default: 3 equal horizontal lites - lockbox in middle panel
    const liteHeight = totalGlassHeight / 3;
    lockboxHeight = liteHeight - frameWidth;
    lockboxYPos = -height/2 + frameWidth + liteHeight + liteHeight/2;
  }
  
  // Lockbox dimensions:
  // - Width: Wider lockbox, stays within door leaf frame
  const lockboxWidth = frameWidth * 4.5; // Wider lockbox
  const lockboxDepth = frameDepth * 1.4;
  
  // Lever handle - simple straight bar as shown in reference
  const leverLength = 0.10; // 100mm horizontal grip  
  const leverDiameter = 0.012; // 12mm diameter bar
  
  // Position lockbox on the handle side (opposite to hinge)
  // Offset inward so it doesn't extend beyond the door leaf frame
  const lockboxXPos = hingeOnLeft ? width/2 - lockboxWidth/2 - 0.001 : -width/2 + lockboxWidth/2 + 0.001;
  
  return (
    <group position={[position[0] + pivotX, position[1], position[2]]}>
      <group rotation={[0, openAngle * rotationDirection, 0]}>
        <group position={[-pivotX, 0, 0]}>
          {/* Left Vertical Frame */}
          <mesh position={[-width/2 + frameWidth/2, 0, 0]} castShadow>
            <boxGeometry args={[frameWidth, height, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>

          {/* Right Vertical Frame */}
          <mesh position={[width/2 - frameWidth/2, 0, 0]} castShadow>
            <boxGeometry args={[frameWidth, height, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>

          {/* Top Frame */}
          <mesh position={[0, height/2 - frameWidth/2, 0]} castShadow>
            <boxGeometry args={[width, frameWidth, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>

          {/* Bottom Frame */}
          <mesh position={[0, -height/2 + frameWidth/2, 0]} castShadow>
            <boxGeometry args={[width, frameWidth, frameDepth]} />
            <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* LOCKBOX - Visible hardware unit spanning between horizontal grids */}
          {/* Color matches frame color (steelColor) */}
          {hasLeverHandle && (
            <group position={[lockboxXPos, lockboxYPos, 0]}>
              {/* Lockbox body - same color as frame, spans full height between horizontal bars */}
              <mesh castShadow>
                <boxGeometry args={[lockboxWidth, lockboxHeight, lockboxDepth]} />
                <meshStandardMaterial color={steelColor} metalness={0.7} roughness={0.3} />
              </mesh>
              
              {/* Lockbox edge trim - gives it depth appearance, matches frame color */}
              <mesh position={[0, 0, lockboxDepth/2 + 0.001]} castShadow>
                <boxGeometry args={[lockboxWidth - 0.004, lockboxHeight - 0.004, 0.003]} />
                <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
              </mesh>
              
              {/* FRONT SIDE - lever handle, matches frame color */}
              <group position={[0, leverYOffset, lockboxDepth/2]}>
                {/* Front Lever Handle */}
                <group position={[0, 0, leverDiameter/2 + 0.008]}>
                  {/* Mounting rosette - matches frame color */}
                  <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
                    <cylinderGeometry args={[0.016, 0.016, 0.008, 24]} />
                    <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
                  </mesh>
                  {/* Horizontal lever bar - matches frame color */}
                  <mesh position={[hingeOnLeft ? -leverLength/2 - 0.008 : leverLength/2 + 0.008, 0, 0]} castShadow>
                    <boxGeometry args={[leverLength, leverDiameter, leverDiameter]} />
                    <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
                  </mesh>
                </group>
                
                {/* Front keyhole - same color as frame */}
                <mesh position={[0, -lockboxHeight * 0.25 - leverYOffset, 0.006]} rotation={[Math.PI/2, 0, 0]} castShadow>
                  <cylinderGeometry args={[0.008, 0.008, 0.005, 24]} />
                  <meshStandardMaterial color={steelColor} metalness={0.9} roughness={0.1} />
                </mesh>
              </group>
              
              {/* BACK SIDE - lever handle (mirrored) */}
              <group position={[0, 0, -lockboxDepth/2]} rotation={[0, Math.PI, 0]}>
                {/* Back edge trim - matches frame color */}
                <mesh position={[0, 0, 0.001]} castShadow>
                  <boxGeometry args={[lockboxWidth - 0.004, lockboxHeight - 0.004, 0.003]} />
                  <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                </mesh>
                
                {/* Back Lever Handle - identical to front, matches frame color */}
                <group position={[0, leverYOffset, leverDiameter/2 + 0.008]}>
                  {/* Mounting rosette */}
                  <mesh rotation={[Math.PI/2, 0, 0]} castShadow>
                    <cylinderGeometry args={[0.016, 0.016, 0.008, 24]} />
                    <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
                  </mesh>
                  {/* Horizontal lever bar - matches frame color */}
                  <mesh position={[hingeOnLeft ? leverLength/2 + 0.008 : -leverLength/2 - 0.008, 0, 0]} castShadow>
                    <boxGeometry args={[leverLength, leverDiameter, leverDiameter]} />
                    <meshStandardMaterial color={steelColor} metalness={0.85} roughness={0.15} />
                  </mesh>
                </group>
                
                {/* Back keyhole */}
                <mesh position={[0, -lockboxHeight * 0.25, 0.006]} rotation={[Math.PI/2, 0, 0]} castShadow>
                  <cylinderGeometry args={[0.008, 0.008, 0.005, 24]} />
                  <meshStandardMaterial color={steelColor} metalness={0.9} roughness={0.1} />
                </mesh>
              </group>
          </group>
        )}

          {/* 3 Horizontal Lites - Equal */}
          {(panel_style === 'Horizontal Lites (3 Equal)' || panel_style === 'Horizontal Lites (3 Divisions)' || panel_style === 'Horizontal Lites') && (
            <>
              {[0, 1, 2].map((i) => {
                const liteHeight = (height - frameWidth * 2) / 3;
                const yPos = -height/2 + frameWidth + liteHeight/2 + i * liteHeight;
                
                return (
                  <group key={`lite-${i}`}>
                    <GlassPanel
                      width={width - frameWidth * 2}
                      height={liteHeight - frameWidth}
                      glassType={glass_type}
                      position={[0, yPos, 0]}
                    />
                    
                    {i < 2 && (
                      <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                        <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                  </group>
                );
              })}
            </>
          )}

          {/* 3 Lites with Smaller Middle */}
          {panel_style === 'Horizontal Lites (3 - Small Middle)' && (
            <>
              {[0, 1, 2].map((i) => {
                const totalHeight = height - frameWidth * 2;
                const liteHeights = [totalHeight * 0.4, totalHeight * 0.2, totalHeight * 0.4];
                const liteHeight = liteHeights[i];
                const yPositions = [
                  -height/2 + frameWidth + liteHeights[0]/2,
                  -height/2 + frameWidth + liteHeights[0] + liteHeights[1]/2,
                  -height/2 + frameWidth + liteHeights[0] + liteHeights[1] + liteHeights[2]/2
                ];
                const yPos = yPositions[i];
                
                return (
                  <group key={`lite-${i}`}>
                    <GlassPanel
                      width={width - frameWidth * 2}
                      height={liteHeight - frameWidth}
                      glassType={glass_type}
                      position={[0, yPos, 0]}
                    />
                    
                    {i < 2 && (
                      <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                        <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                  </group>
                );
              })}
            </>
          )}

          {/* 4 Horizontal Lites - Equal */}
          {panel_style === 'Horizontal Lites (4 Equal)' && (
            <>
              {[0, 1, 2, 3].map((i) => {
                const liteHeight = (height - frameWidth * 2) / 4;
                const yPos = -height/2 + frameWidth + liteHeight/2 + i * liteHeight;
                
                return (
                  <group key={`lite-${i}`}>
                    <GlassPanel
                      width={width - frameWidth * 2}
                      height={liteHeight - frameWidth}
                      glassType={glass_type}
                      position={[0, yPos, 0]}
                    />
                    
                    {i < 3 && (
                      <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                        <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                  </group>
                );
              })}
            </>
          )}

          {/* Grid Pattern - 2x2 */}
          {panel_style === 'Grid Pattern (2x2)' && (
            <>
              {Array.from({ length: 4 }).map((_, idx) => {
                const row = Math.floor(idx / 2);
                const col = idx % 2;
                const panelWidth = (width - frameWidth * 2) / 2;
                const panelHeight = (height - frameWidth * 2) / 2;
                const xPos = -width/2 + frameWidth + panelWidth/2 + col * panelWidth;
                const yPos = height/2 - frameWidth - panelHeight/2 - row * panelHeight;
                
                return (
                  <group key={`grid-${idx}`}>
                    <GlassPanel
                      width={panelWidth - frameWidth}
                      height={panelHeight - frameWidth}
                      glassType={glass_type}
                      position={[xPos, yPos, 0]}
                    />
                    
                    {col === 0 && (
                      <mesh position={[xPos + panelWidth/2, yPos, 0]} castShadow>
                        <boxGeometry args={[frameWidth, panelHeight, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                    
                    {row === 0 && (
                      <mesh position={[xPos, yPos - panelHeight/2, 0]} castShadow>
                        <boxGeometry args={[panelWidth, frameWidth, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                  </group>
                );
              })}
            </>
          )}

          {/* Grid Pattern - 3x2 */}
          {panel_style === 'Grid Pattern (3x2)' && (
            <>
              {Array.from({ length: 6 }).map((_, idx) => {
                const row = Math.floor(idx / 2);
                const col = idx % 2;
                const panelWidth = (width - frameWidth * 2) / 2;
                const panelHeight = (height - frameWidth * 2) / 3;
                const xPos = -width/2 + frameWidth + panelWidth/2 + col * panelWidth;
                const yPos = height/2 - frameWidth - panelHeight/2 - row * panelHeight;
                
                return (
                  <group key={`grid-${idx}`}>
                    <GlassPanel
                      width={panelWidth - frameWidth}
                      height={panelHeight - frameWidth}
                      glassType={glass_type}
                      position={[xPos, yPos, 0]}
                    />
                    
                    {col === 0 && (
                      <mesh position={[xPos + panelWidth/2, yPos, 0]} castShadow>
                        <boxGeometry args={[frameWidth, panelHeight, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                    
                    {row < 2 && (
                      <mesh position={[xPos, yPos - panelHeight/2, 0]} castShadow>
                        <boxGeometry args={[panelWidth, frameWidth, frameDepth]} />
                        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                      </mesh>
                    )}
                  </group>
                );
              })}
            </>
          )}

          {/* Arched in Square - Semi-circular arch bar inscribed inside the glass opening */}
          {/* The arch is a frame bar (same profile as horizontal dividers) following an arc path */}
          {hasArchedInSquare && (
            <>
              {(() => {
                // Inner glass opening dimensions (inside the door frame)
                const innerGlassWidth = width - frameWidth * 2;
                
                // Arch radius - matches half the glass width so ends touch the vertical frame
                const archRadius = isDoubleDoor ? innerGlassWidth : innerGlassWidth / 2;
                
                // Y position: arch apex at top of glass, center below
                const innerGlassTop = height / 2 - frameWidth;
                const archCenterY = innerGlassTop - archRadius;
                
                // X position: center at meeting edge for double, center for single
                const archCenterX = isDoubleDoor
                  ? (hingeOnLeft ? innerGlassWidth / 2 : -innerGlassWidth / 2)
                  : 0;
                
                // Create arch using multiple box segments to approximate the curve
                const segments = isDoubleDoor ? 12 : 24;
                const arcAngle = isDoubleDoor ? Math.PI / 2 : Math.PI;
                
                let startAngle;
                if (isDoubleDoor) {
                  startAngle = hingeOnLeft ? Math.PI / 2 : 0;
                } else {
                  startAngle = 0;
                }
                
                const archSegments = [];
                for (let i = 0; i < segments; i++) {
                  const angle1 = startAngle + (i / segments) * arcAngle;
                  const angle2 = startAngle + ((i + 1) / segments) * arcAngle;
                  const midAngle = (angle1 + angle2) / 2;
                  
                  const segX = Math.cos(midAngle) * archRadius;
                  const segY = Math.sin(midAngle) * archRadius;
                  
                  // Base segment length
                  let segLength = 2 * archRadius * Math.sin((angle2 - angle1) / 2) * 1.05;
                  
                  // For end segments, extend them longer so they flow into/behind the vertical frame
                  const isFirstSegment = i === 0;
                  const isLastSegment = i === segments - 1;
                  if (isFirstSegment || isLastSegment) {
                    segLength *= 1.5; // Extend end segments to overlap into the frame
                  }
                  
                  const segRotation = midAngle + Math.PI / 2;
                  
                  archSegments.push(
                    <mesh
                      key={`arch-seg-${i}`}
                      position={[segX, segY, 0]}
                      rotation={[0, 0, segRotation]}
                      castShadow
                    >
                      <boxGeometry args={[segLength, frameWidth, frameDepth]} />
                      <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                    </mesh>
                  );
                }
                
                return (
                  <group position={[archCenterX, archCenterY, 0]}>
                    {archSegments}
                  </group>
                );
              })()}
            </>
          )}
        </group>
      </group>
    </group>
  );
};

// Sidelite Panel - Fixed glazed panel with same grid pattern as door but no hardware
const SidelitePanel = ({ 
  width, 
  height, 
  frameWidth, 
  frameDepth, 
  steelColor, 
  glass_type, 
  panel_style,
  position
}) => {
  return (
    <group position={position}>
      {/* Left Vertical Frame */}
      <mesh position={[-width/2 + frameWidth/2, 0, 0]} castShadow>
        <boxGeometry args={[frameWidth, height, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Right Vertical Frame */}
      <mesh position={[width/2 - frameWidth/2, 0, 0]} castShadow>
        <boxGeometry args={[frameWidth, height, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Top Frame */}
      <mesh position={[0, height/2 - frameWidth/2, 0]} castShadow>
        <boxGeometry args={[width, frameWidth, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Bottom Frame */}
      <mesh position={[0, -height/2 + frameWidth/2, 0]} castShadow>
        <boxGeometry args={[width, frameWidth, frameDepth]} />
        <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Glass Panels - same style as door but no hardware */}
      {/* 3 Horizontal Lites - Equal */}
      {(panel_style === 'Horizontal Lites (3 Equal)' || panel_style === 'Horizontal Lites (3 Divisions)' || panel_style === 'Horizontal Lites') && (
        <>
          {[0, 1, 2].map((i) => {
            const liteHeight = (height - frameWidth * 2) / 3;
            const yPos = -height/2 + frameWidth + liteHeight/2 + i * liteHeight;
            
            return (
              <group key={`sidelite-lite-${i}`}>
                <GlassPanel
                  width={width - frameWidth * 2}
                  height={liteHeight - frameWidth}
                  glassType={glass_type}
                  position={[0, yPos, 0]}
                />
                
                {i < 2 && (
                  <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                    <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
              </group>
            );
          })}
        </>
      )}

      {/* 3 Lites with Smaller Middle */}
      {panel_style === 'Horizontal Lites (3 - Small Middle)' && (
        <>
          {[0, 1, 2].map((i) => {
            const totalHeight = height - frameWidth * 2;
            const liteHeights = [totalHeight * 0.4, totalHeight * 0.2, totalHeight * 0.4];
            const liteHeight = liteHeights[i];
            const yPositions = [
              -height/2 + frameWidth + liteHeights[0]/2,
              -height/2 + frameWidth + liteHeights[0] + liteHeights[1]/2,
              -height/2 + frameWidth + liteHeights[0] + liteHeights[1] + liteHeights[2]/2
            ];
            const yPos = yPositions[i];
            
            return (
              <group key={`sidelite-lite-${i}`}>
                <GlassPanel
                  width={width - frameWidth * 2}
                  height={liteHeight - frameWidth}
                  glassType={glass_type}
                  position={[0, yPos, 0]}
                />
                
                {i < 2 && (
                  <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                    <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
              </group>
            );
          })}
        </>
      )}

      {/* 4 Horizontal Lites - Equal */}
      {panel_style === 'Horizontal Lites (4 Equal)' && (
        <>
          {[0, 1, 2, 3].map((i) => {
            const liteHeight = (height - frameWidth * 2) / 4;
            const yPos = -height/2 + frameWidth + liteHeight/2 + i * liteHeight;
            
            return (
              <group key={`sidelite-lite-${i}`}>
                <GlassPanel
                  width={width - frameWidth * 2}
                  height={liteHeight - frameWidth}
                  glassType={glass_type}
                  position={[0, yPos, 0]}
                />
                
                {i < 3 && (
                  <mesh position={[0, yPos + liteHeight/2, 0]} castShadow>
                    <boxGeometry args={[width - frameWidth * 2, frameWidth, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
              </group>
            );
          })}
        </>
      )}

      {/* Grid Pattern - 2x2 */}
      {panel_style === 'Grid Pattern (2x2)' && (
        <>
          {Array.from({ length: 4 }).map((_, idx) => {
            const row = Math.floor(idx / 2);
            const col = idx % 2;
            const panelWidth = (width - frameWidth * 2) / 2;
            const panelHeight = (height - frameWidth * 2) / 2;
            const xPos = -width/2 + frameWidth + panelWidth/2 + col * panelWidth;
            const yPos = height/2 - frameWidth - panelHeight/2 - row * panelHeight;
            
            return (
              <group key={`sidelite-grid-${idx}`}>
                <GlassPanel
                  width={panelWidth - frameWidth}
                  height={panelHeight - frameWidth}
                  glassType={glass_type}
                  position={[xPos, yPos, 0]}
                />
                
                {col === 0 && (
                  <mesh position={[xPos + panelWidth/2, yPos, 0]} castShadow>
                    <boxGeometry args={[frameWidth, panelHeight, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
                
                {row === 0 && (
                  <mesh position={[xPos, yPos - panelHeight/2, 0]} castShadow>
                    <boxGeometry args={[panelWidth, frameWidth, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
              </group>
            );
          })}
        </>
      )}

      {/* Grid Pattern - 3x2 */}
      {panel_style === 'Grid Pattern (3x2)' && (
        <>
          {Array.from({ length: 6 }).map((_, idx) => {
            const row = Math.floor(idx / 2);
            const col = idx % 2;
            const panelWidth = (width - frameWidth * 2) / 2;
            const panelHeight = (height - frameWidth * 2) / 3;
            const xPos = -width/2 + frameWidth + panelWidth/2 + col * panelWidth;
            const yPos = height/2 - frameWidth - panelHeight/2 - row * panelHeight;
            
            return (
              <group key={`sidelite-grid-${idx}`}>
                <GlassPanel
                  width={panelWidth - frameWidth}
                  height={panelHeight - frameWidth}
                  glassType={glass_type}
                  position={[xPos, yPos, 0]}
                />
                
                {col === 0 && (
                  <mesh position={[xPos + panelWidth/2, yPos, 0]} castShadow>
                    <boxGeometry args={[frameWidth, panelHeight, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
                
                {row < 2 && (
                  <mesh position={[xPos, yPos - panelHeight/2, 0]} castShadow>
                    <boxGeometry args={[panelWidth, frameWidth, frameDepth]} />
                    <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
                  </mesh>
                )}
              </group>
            );
          })}
        </>
      )}
    </group>
  );
};

export const DoorModel = ({ configuration, isOpen = false }) => {
  const groupRef = useRef();
  
  const { profile, width, height, color, glass_type, door_type, panel_style, transom, threshold, hardware, sidelite_left, sidelite_right, arched_in_square, true_arch_frame } = configuration;
  const profileSpec = PROFILE_SPECS[profile] || PROFILE_SPECS.W20;
  
  const doorWidth = width / 1000;
  const doorHeight = height / 1000;
  const frameWidth = profileSpec.frameWidth;
  const frameDepth = profileSpec.frameDepth;
  
  const steelColor = COLOR_MAP[color] || COLOR_MAP['Black'];
  
  const isDouble = door_type === 'Double';
  const hasTransom = transom === 'Yes';
  const hasThreshold = threshold !== 'No Threshold';
  const hasSideliteLeft = sidelite_left === 'Yes';
  const hasSideliteRight = sidelite_right === 'Yes';
  const hasArchedInSquare = arched_in_square === 'Yes';
  const hasTrueArchFrame = true_arch_frame === 'Yes';
  
  // Transom height from configuration (in mm), convert to meters
  const transomHeightValue = (configuration.transomHeight || 400) / 1000;
  const jambWidth = frameWidth * 1.5;
  
  // Sidelite width - proportional to door width (about 1/3 of a single door leaf)
  const sideliteWidth = isDouble ? doorWidth / 4 : doorWidth / 3;
  
  // Calculate total unit width including sidelites
  const totalWidth = doorWidth + (hasSideliteLeft ? sideliteWidth : 0) + (hasSideliteRight ? sideliteWidth : 0);
  
  // Calculate offset to center the entire unit (door + sidelites) within the outer jamb
  // The door center needs to shift based on which sidelites are present
  // When only left sidelite: door center shifts RIGHT by sideliteWidth/2
  // When only right sidelite: door center shifts LEFT by sideliteWidth/2  
  // When both or neither: door stays centered (no shift)
  const doorCenterOffset = ((hasSideliteLeft ? sideliteWidth : 0) - (hasSideliteRight ? sideliteWidth : 0)) / 2;
  
  // Check if hardware requires a lock plate (lever handle options)
  const hasLeverHandle = hardware && (hardware.includes('Lever'));
  
  // When True Arch Frame is enabled, render the complete arched door unit
  if (hasTrueArchFrame) {
    return (
      <group ref={groupRef} position={[0, 0, 0]}>
        <TrueArchDoorUnit
          totalWidth={totalWidth}
          doorWidth={doorWidth}
          doorHeight={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          glass_type={glass_type}
          panel_style={panel_style}
          isDouble={isDouble}
          hasThreshold={hasThreshold}
          hasSideliteLeft={hasSideliteLeft}
          hasSideliteRight={hasSideliteRight}
          sideliteWidth={sideliteWidth}
          isOpen={isOpen}
          hasLeverHandle={hasLeverHandle}
        />
      </group>
    );
  }
  
  if (isDouble) {
    const singleWidth = doorWidth / 2;
    
    return (
      <group ref={groupRef} position={[0, 0, 0]}>
        {/* Outer Jamb */}
        <OuterJamb
          width={totalWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          hasThreshold={hasThreshold}
          hasTransom={hasTransom}
          transomHeight={transomHeightValue}
        />
        
        {/* UNIFIED TRANSOM - One continuous panel spanning entire width */}
        {hasTransom && (
          <group position={[0, doorHeight/2 + transomHeightValue/2, 0]}>
            {/* Single horizontal bar at bottom of transom (separates transom from door/sidelites) */}
            <mesh position={[0, -transomHeightValue/2 + frameWidth/2, 0]} castShadow>
              <boxGeometry args={[totalWidth, frameWidth, frameDepth]} />
              <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
            </mesh>
            
            {/* Single continuous transom glass - spans full width */}
            <GlassPanel
              width={totalWidth - frameWidth * 2}
              height={transomHeightValue - frameWidth * 2}
              glassType={glass_type}
              position={[0, 0, 0]}
            />
          </group>
        )}
        
        {/* Left Sidelite - positioned at left edge of outer jamb */}
        {hasSideliteLeft && (
          <group position={[-totalWidth/2 + sideliteWidth/2, 0, 0]}>
            <SidelitePanel
              width={sideliteWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              position={[0, 0, 0]}
            />
          </group>
        )}
          
        {/* Right Sidelite - positioned at right edge of outer jamb */}
        {hasSideliteRight && (
          <group position={[totalWidth/2 - sideliteWidth/2, 0, 0]}>
            <SidelitePanel
              width={sideliteWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              position={[0, 0, 0]}
            />
          </group>
        )}
        
        {/* Door content group - offset to keep door centered properly */}
        <group position={[doorCenterOffset, 0, 0]}>
          {/* Door leafs - with integrated lockbox and hardware */}
          <group position={[0, 0, 0]}>
            <DoorPanel
              width={singleWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              isOpen={isOpen}
              position={[-singleWidth/2, 0, 0]}
              hingeOnLeft={true}
              hasLeverHandle={hasLeverHandle}
              isDoubleDoor={true}
              hasArchedInSquare={hasArchedInSquare}
            />
            
            <DoorPanel
              width={singleWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              isOpen={isOpen}
              position={[singleWidth/2, 0, 0]}
              hingeOnLeft={false}
              hasLeverHandle={hasLeverHandle}
              isDoubleDoor={true}
              hasArchedInSquare={hasArchedInSquare}
            />
          </group>
        </group>
      </group>
    );
  } else {
    return (
      <group ref={groupRef} position={[0, 0, 0]}>
        {/* Outer Jamb */}
        <OuterJamb
          width={totalWidth}
          height={doorHeight}
          frameWidth={frameWidth}
          frameDepth={frameDepth}
          steelColor={steelColor}
          hasThreshold={hasThreshold}
          hasTransom={hasTransom}
          transomHeight={transomHeightValue}
        />
        
        {/* UNIFIED TRANSOM - One continuous panel spanning entire width */}
        {hasTransom && (
          <group position={[0, doorHeight/2 + transomHeightValue/2, 0]}>
            {/* Single horizontal bar at bottom of transom (separates transom from door/sidelites) */}
            <mesh position={[0, -transomHeightValue/2 + frameWidth/2, 0]} castShadow>
              <boxGeometry args={[totalWidth, frameWidth, frameDepth]} />
              <meshStandardMaterial color={steelColor} metalness={0.8} roughness={0.2} />
            </mesh>
            
            {/* Single continuous transom glass - spans full width */}
            <GlassPanel
              width={totalWidth - frameWidth * 2}
              height={transomHeightValue - frameWidth * 2}
              glassType={glass_type}
              position={[0, 0, 0]}
            />
          </group>
        )}
        
        {/* Left Sidelite - positioned at left edge of outer jamb */}
        {hasSideliteLeft && (
          <group position={[-totalWidth/2 + sideliteWidth/2, 0, 0]}>
            <SidelitePanel
              width={sideliteWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              position={[0, 0, 0]}
            />
          </group>
        )}
          
        {/* Right Sidelite - positioned at right edge of outer jamb */}
        {hasSideliteRight && (
          <group position={[totalWidth/2 - sideliteWidth/2, 0, 0]}>
            <SidelitePanel
              width={sideliteWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              position={[0, 0, 0]}
            />
          </group>
        )}
        
        {/* Door content group - offset to keep door centered properly */}
        <group position={[doorCenterOffset, 0, 0]}>
          <group position={[0, 0, 0]}>
            <DoorPanel
              width={doorWidth}
              height={doorHeight}
              frameWidth={frameWidth}
              frameDepth={frameDepth}
              steelColor={steelColor}
              glass_type={glass_type}
              panel_style={panel_style}
              isOpen={isOpen}
              position={[0, 0, 0]}
              hingeOnLeft={true}
              hasLeverHandle={hasLeverHandle}
              isDoubleDoor={false}
              hasArchedInSquare={hasArchedInSquare}
            />
          </group>
        </group>
      </group>
    );
  }
};

export default DoorModel;
